import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Plus, Receipt, Route, Search, Trash2, Truck } from 'lucide-react';
import * as XLSX from 'xlsx';
import AppBar from 'components/AppBar';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Skeleton } from 'components/ui/skeleton';
import {
  fetchLocalTransportTrucking,
  createLocalTransportTrucking,
  updateLocalTransportTrucking,
  deleteLocalTransportTrucking,
  normalizeTruckingInvoiceNo
} from '../services/localTransportTruckingService';
import client, { DATABASE_ID, COLLECTION_ID_LOCAL_TRANSPORT_TRUCKING } from '../config/appwrite';
import './LocalTransportationTrucking.css';
import { useAuthSession } from '../contexts/AuthSessionContext';
import { getLockedLotFilter, isReadOnlySession } from '../services/authService';

const VAT_RATE = 0.18;

const STATUS_OPTIONS = ['', 'Pending', 'Paid'];

const LOT_8 = 'LOT 8';
const LOT_10 = 'LOT 10';
const LOT_OPTIONS = [LOT_8, LOT_10];
const TAB_OVERVIEW = 'Spending by Material';
const TAB_OPTIONS = [...LOT_OPTIONS, TAB_OVERVIEW];

/** Palette for rows that share the same invoice number. */
const INVOICE_RIBBON_COLORS = [
  '#10b981',
  '#f59e0b',
  '#185fa5',
  '#8b5cf6',
  '#ec4899',
  '#06b6d4',
  '#854f0b',
  '#3b6d11'
];

const inputSizeFromValue = (value, min = 3, max = 40) =>
  Math.min(max, Math.max(min, String(value ?? '').trim().length || min));

/** Invoice cells with a left colour ribbon when the number is shared by 2+ rows. */
function LttRibbonCell({ ribbonColor, className, children }) {
  return (
    <td className={className}>
      <div
        className={`ltt-ribbon-cell${ribbonColor ? ' ltt-ribbon-cell--active' : ''}`}
        style={ribbonColor ? { '--ltt-ribbon-color': ribbonColor } : undefined}
      >
        {ribbonColor ? (
          <span
            className="ltt-group-ribbon"
            style={{ backgroundColor: ribbonColor }}
            aria-hidden="true"
            title="Same invoice number as other row(s)"
          />
        ) : null}
        <div className="ltt-ribbon-cell-body">{children}</div>
      </div>
    </td>
  );
}

/** Records saved before the LOT split have no lot value — treat them as LOT 8. */
const effectiveLot = (row) => row.lot || LOT_8;

/** Starter rows from the reference table — seeded once when the collection is empty. */
const SEED_ROWS = [
  { transporter: 'Majestic', po: '', materialTransported: 'Cables', invoiceNo: 'MT/079987301', noOfTrucks: 1, pricePerTrip: 2600000, vatApplies: false, destination: 'Dar - Morogoro' },
  { transporter: 'Majestic', po: '', materialTransported: 'Cables', invoiceNo: 'MT/0720626', noOfTrucks: 3, pricePerTrip: 2600000, vatApplies: false, destination: 'Dar - Morogoro' },
  { transporter: 'Majestic', po: 'No po', materialTransported: 'Cables', invoiceNo: '551001230', noOfTrucks: 1, pricePerTrip: 2000000, vatApplies: false, destination: 'Dar - Morogoro' },
  { transporter: 'Majestic', po: 'No po', materialTransported: 'Fittings', invoiceNo: '551001230', noOfTrucks: 2, pricePerTrip: 3000000, vatApplies: false, destination: 'Manyara - Morogoro' },
  { transporter: 'Majestic', po: 'No po', materialTransported: 'Fittings', invoiceNo: '551001230', noOfTrucks: 3, pricePerTrip: 2900000, vatApplies: false, destination: 'Dar (Kigamboni) - Morogoro' },
  { transporter: 'Majestic', po: 'No po', materialTransported: 'Fittings', invoiceNo: '', noOfTrucks: 1, pricePerTrip: 3000000, vatApplies: false, destination: 'Manyara - Morogoro' },
  { transporter: 'Majestic', po: 'No po', materialTransported: 'Fittings', invoiceNo: '', noOfTrucks: 1, pricePerTrip: 1800000, vatApplies: false, destination: 'Dar (Kigamboni) - Morogoro' },
  { transporter: 'Majestic', po: 'No po', materialTransported: 'Concrete poles', invoiceNo: '', noOfTrucks: 2, pricePerTrip: 2700000, vatApplies: false, destination: 'Dar (Mkulanga) - Morogoro' },
  { transporter: 'Lala', po: 'No po', materialTransported: 'Concrete poles', invoiceNo: '551001235', noOfTrucks: 2, pricePerTrip: 2800000, vatApplies: false, destination: 'Dar (Mkulanga) - Morogoro' },
  { transporter: 'Master one', po: '', materialTransported: 'Transformers', invoiceNo: '', noOfTrucks: 1, pricePerTrip: 2800000, vatApplies: false, destination: 'Dar - Morogoro' },
  { transporter: 'Master one', po: 'No po', materialTransported: 'Concrete poles', invoiceNo: '', noOfTrucks: 2, pricePerTrip: 2700000, vatApplies: false, destination: 'Dar (Mkulanga) - Morogoro' }
];

/** Canonical material labels for Spending by Material (merge typos / variants). */
const normalizeMaterialLabel = (material) => {
  const raw = String(material || '').trim();
  if (!raw || raw === '—') return '—';
  const sub = raw.toLowerCase();
  if (sub.includes('concret') || sub.includes('concreat')) return 'Concrete poles';
  if (sub.includes('wooden pole') || sub === 'poles' || sub === 'pole') return 'Wooden poles';
  if (sub.includes('fitting')) return 'Fittings';
  if (sub.includes('cable')) return 'Cables';
  if (sub.includes('transformer')) return 'Transformers';
  if (sub.includes('meter')) return 'Meters';
  return raw;
};

const emptyRow = (orderIndex, lot = LOT_8) => ({
  lot,
  transporter: '',
  po: '',
  materialTransported: '',
  invoiceNo: '',
  noOfTrucks: 0,
  pricePerTrip: 0,
  vatApplies: false,
  destination: '',
  dnNo: '',
  dnDate: '',
  exemptionName: '',
  status: '',
  datePaid: '',
  orderIndex
});

const rowTotalAmount = (row) => (Number(row.noOfTrucks) || 0) * (Number(row.pricePerTrip) || 0);
const rowVat = (row) => (row.vatApplies ? rowTotalAmount(row) * VAT_RATE : 0);
const rowTotalWithVat = (row) => rowTotalAmount(row) + rowVat(row);

const formatMoney = (value) => {
  const num = Number(value) || 0;
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function LttPageSkeleton() {
  return (
    <div className="ltt-skeleton" aria-busy="true" aria-label="Loading transport data">
      <section className="ltt-intro" aria-hidden>
        <div className="ltt-intro-text">
          <Skeleton className="h-7 w-72 max-w-full" />
          <Skeleton className="mt-2.5 h-4 w-96 max-w-full" />
        </div>
        <div className="ltt-intro-actions">
          <Skeleton className="h-9 w-9 rounded-lg" />
          <Skeleton className="h-9 w-[7rem] rounded-lg" />
        </div>
      </section>
      <div className="ltt-lot-tabs" aria-hidden>
        <Skeleton className="h-9 w-24 rounded-md" />
        <Skeleton className="h-9 w-24 rounded-md" />
        <Skeleton className="h-9 w-40 rounded-md" />
      </div>
      <div className="ltt-summary-cards" aria-hidden>
        {[0, 1, 2].map((i) => (
          <div key={i} className="ltt-summary-card ltt-summary-card--skel">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <Skeleton className="mt-3 h-4 w-24" />
            <Skeleton className="mt-2 h-6 w-36" />
            <Skeleton className="mt-4 h-3 w-full" />
          </div>
        ))}
      </div>
      <div className="ltt-toolbar" aria-hidden>
        <Skeleton className="h-9 w-full max-w-md rounded-lg" />
      </div>
      <div className="ltt-table-shell" aria-hidden>
        <div className="ltt-skel-rows">
          {Array.from({ length: 6 }, (_, r) => (
            <Skeleton key={r} className="h-10 w-full rounded-md" />
          ))}
        </div>
      </div>
    </div>
  );
}

function LocalTransportationTrucking() {
  const session = useAuthSession();
  const lockedLotFilter = getLockedLotFilter(session);
  const isReadOnly = isReadOnlySession(session);
  const isLot10Only = Boolean(lockedLotFilter);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeLot, setActiveLot] = useState(isLot10Only ? LOT_10 : LOT_8);
  const [overviewLotFilter, setOverviewLotFilter] = useState('all');
  const [focusedCell, setFocusedCell] = useState(null);
  const saveTimers = useRef({});

  useEffect(() => {
    if (isLot10Only) setActiveLot(LOT_10);
  }, [isLot10Only]);

  const visibleTabs = isLot10Only ? [LOT_10] : TAB_OPTIONS;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const existing = await fetchLocalTransportTrucking();
        if (cancelled) return;
        if (existing && existing.length > 0) {
          setRows(existing);
        } else {
          // Seed the reference table rows into the empty collection.
          const seeded = [];
          for (let i = 0; i < SEED_ROWS.length; i += 1) {
            // eslint-disable-next-line no-await-in-loop
            const created = await createLocalTransportTrucking({ ...emptyRow(i), ...SEED_ROWS[i] });
            seeded.push(created);
          }
          if (!cancelled) setRows(seeded);
        }
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          setError(err?.message || String(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      const timersRef = saveTimers;
      Object.values(timersRef.current).forEach((t) => t && clearTimeout(t));
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const channel = `databases.${DATABASE_ID}.collections.${COLLECTION_ID_LOCAL_TRANSPORT_TRUCKING}.documents`;

    const refreshRows = async () => {
      try {
        const existing = await fetchLocalTransportTrucking();
        if (!cancelled) setRows(existing);
      } catch (err) {
        console.error('Local transport live refresh failed:', err);
      }
    };

    refreshRows();
    const pollId = setInterval(refreshRows, 20000);

    const unsubscribe = client.subscribe(channel, () => {
      refreshRows();
    });

    return () => {
      cancelled = true;
      clearInterval(pollId);
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  const lotCounts = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        const key = effectiveLot(row);
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      },
      { [LOT_8]: 0, [LOT_10]: 0 }
    );
  }, [rows]);

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const byLot = rows.filter((row) => effectiveLot(row) === activeLot);
    if (!query) return byLot;
    return byLot.filter((row) =>
      [
        row.transporter,
        row.po,
        row.materialTransported,
        row.invoiceNo,
        row.destination,
        row.dnNo,
        row.status
      ].some((v) => String(v || '').toLowerCase().includes(query))
    );
  }, [rows, searchQuery, activeLot]);

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, row) => {
        acc.trucks += Number(row.noOfTrucks) || 0;
        acc.totalAmount += rowTotalAmount(row);
        acc.vat += rowVat(row);
        acc.totalWithVat += rowTotalWithVat(row);
        return acc;
      },
      { trucks: 0, totalAmount: 0, vat: 0, totalWithVat: 0 }
    );
  }, [filteredRows]);

  const isOverview = activeLot === TAB_OVERVIEW;

  // Spending overview across ALL lots — aggregated by material transported.
  const overviewRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    let source = overviewLotFilter === 'all' ? rows : rows.filter((row) => effectiveLot(row) === overviewLotFilter);
    if (query) {
      source = source.filter((row) =>
        [row.materialTransported, row.transporter, row.destination, effectiveLot(row)].some((v) =>
          String(v || '').toLowerCase().includes(query)
        )
      );
    }

    const map = new Map();
    source.forEach((row) => {
      const material = normalizeMaterialLabel(row.materialTransported);
      const key = material.toLowerCase();
      const spend = rowTotalAmount(row);
      if (spend <= 0) return;
      if (!map.has(key)) {
        map.set(key, { material, trips: 0, totalAmount: 0, totalWithVat: 0 });
      }
      const g = map.get(key);
      g.trips += Number(row.noOfTrucks) || 0;
      g.totalAmount += spend;
      g.totalWithVat += rowTotalWithVat(row);
    });

    // Highest spend first — this is a "how much am I spending" view.
    return Array.from(map.values()).sort((a, b) => b.totalAmount - a.totalAmount);
  }, [rows, searchQuery, overviewLotFilter]);

  const overviewTotals = useMemo(
    () =>
      overviewRows.reduce(
        (acc, g) => {
          acc.trips += g.trips;
          acc.totalAmount += g.totalAmount;
          acc.totalWithVat += g.totalWithVat;
          return acc;
        },
        { trips: 0, totalAmount: 0, totalWithVat: 0 }
      ),
    [overviewRows]
  );

  const cards = isOverview
    ? { trips: overviewTotals.trips, totalAmount: overviewTotals.totalAmount, totalWithVat: overviewTotals.totalWithVat }
    : { trips: totals.trucks, totalAmount: totals.totalAmount, totalWithVat: totals.totalWithVat };

  const invoiceRibbonColors = useMemo(() => {
    const counts = new Map();
    rows.forEach((row) => {
      const key = normalizeTruckingInvoiceNo(row.invoiceNo);
      if (key) counts.set(key, (counts.get(key) || 0) + 1);
    });
    const colorByKey = new Map();
    let idx = 0;
    counts.forEach((count, key) => {
      if (count >= 2) {
        colorByKey.set(key, INVOICE_RIBBON_COLORS[idx % INVOICE_RIBBON_COLORS.length]);
        idx += 1;
      }
    });
    return colorByKey;
  }, [rows]);

  const duplicateInvoiceRowCount = useMemo(() => {
    const counts = new Map();
    rows.forEach((row) => {
      const key = normalizeTruckingInvoiceNo(row.invoiceNo);
      if (key) counts.set(key, (counts.get(key) || 0) + 1);
    });
    return rows.filter((row) => {
      const key = normalizeTruckingInvoiceNo(row.invoiceNo);
      return key && (counts.get(key) || 0) >= 2;
    }).length;
  }, [rows]);

  const scheduleSave = (documentId, nextRow) => {
    if (!documentId || isReadOnly) return;
    if (saveTimers.current[documentId]) {
      clearTimeout(saveTimers.current[documentId]);
    }
    saveTimers.current[documentId] = setTimeout(async () => {
      try {
        await updateLocalTransportTrucking(documentId, nextRow);
      } catch (err) {
        console.error('Failed to save trucking row:', err);
        setError('Failed to save changes. Please try again.');
      }
    }, 600);
  };

  const handleChange = (documentId, field, value) => {
    if (isReadOnly) return;
    if (field === 'invoiceNo') {
      value = String(value).trim();
    }

    setRows((prev) => {
      const next = prev.map((row) => {
        if (row.documentId !== documentId) return row;
        const updated = { ...row, [field]: value };
        if (field === 'status' && value !== 'Paid') {
          updated.datePaid = '';
        }
        scheduleSave(documentId, updated);
        return updated;
      });
      return next;
    });
  };

  const handleAddRow = async () => {
    try {
      const nextIndex = rows.length ? Math.max(...rows.map((r) => r.orderIndex || 0)) + 1 : 0;
      const created = await createLocalTransportTrucking(emptyRow(nextIndex, activeLot));
      setRows((prev) => [...prev, created]);
    } catch (err) {
      console.error(err);
      window.alert(err?.message || 'Failed to add row.');
    }
  };

  const handleDeleteRow = async (row) => {
    const label = row.transporter || row.materialTransported || 'this row';
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    try {
      if (row.documentId) await deleteLocalTransportTrucking(row.documentId);
      setRows((prev) => prev.filter((r) => r.documentId !== row.documentId));
    } catch (err) {
      console.error(err);
      window.alert(err?.message || 'Failed to delete row.');
    }
  };

  const handleExportExcel = () => {
    if (isOverview) {
      const overviewSheet = overviewRows.map((g) => ({
        'Material Transported': g.material,
        Trips: g.trips,
        'Total Spend': g.totalAmount,
        'Total Spend (incl. VAT)': g.totalWithVat,
        '% of Spend': overviewTotals.totalAmount
          ? Number(((g.totalAmount / overviewTotals.totalAmount) * 100).toFixed(1))
          : 0
      }));
      const wbOverview = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wbOverview, XLSX.utils.json_to_sheet(overviewSheet), 'Spending by Material');
      const dateStrOverview = new Date().toISOString().split('T')[0];
      const lotTag = overviewLotFilter === 'all' ? 'All_LOTs' : overviewLotFilter.replace(/\s+/g, '_');
      XLSX.writeFile(wbOverview, `Local_Transport_Spending_By_Material_${lotTag}_${dateStrOverview}.xlsx`);
      return;
    }
    const sheet = filteredRows.map((row) => ({
      Transporter: row.transporter,
      PO: row.po,
      'Material Transported': row.materialTransported,
      'Invoice No': row.invoiceNo,
      Trips: Number(row.noOfTrucks) || 0,
      'Price per Trip': rowVatlessPrice(row),
      'Total Amount': rowTotalAmount(row),
      VAT: rowVat(row),
      'Total Amount (incl. VAT)': rowTotalWithVat(row),
      Destination: row.destination,
      'DN No': row.dnNo,
      'DN Date': row.dnDate,
      Status: row.status,
      'Date Paid': row.datePaid
    }));
    const wb = XLSX.utils.book_new();
    const lotLabel = activeLot.replace(/\s+/g, '_');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet), activeLot);
    const dateStr = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `Local_Transportation_Trucking_${lotLabel}_${dateStr}.xlsx`);
  };

  const rowVatlessPrice = (row) => Number(row.pricePerTrip) || 0;

  const numericDisplay = (documentId, field, rawValue) => {
    const cellKey = `${documentId}-${field}`;
    const isFocused = focusedCell === cellKey;
    if (isFocused) return rawValue === 0 ? '' : String(rawValue);
    const num = Number(rawValue) || 0;
    if (num === 0) return '';
    return field === 'noOfTrucks' ? String(num) : num.toLocaleString('en-US');
  };

  return (
    <div className="ltt-page">
      <div className="ltt-scroll">
        <div className="ltt-panel">
          <AppBar
            variant="inset"
            breadcrumbs={[
              { label: 'Platform', hideOnMobile: true },
              { label: 'Sagemcom', to: '/sagemcom' },
              { label: 'Logistics', to: '/sagemcom/logistics', hideOnMobile: true },
              { label: 'Local transportation trucking' },
            ]}
          />

          {loading && rows.length === 0 && !error ? (
            <LttPageSkeleton />
          ) : (
            <>
              <section className="ltt-intro" aria-label="Local transportation overview">
                <div className="ltt-intro-text">
                  <h1 className="ltt-intro-title">Local transportation trucking</h1>
                  <p className="ltt-intro-subtitle">
                    Track material transport trips, amounts, VAT, and payments by LOT
                  </p>
                </div>
                <div className="ltt-intro-actions">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="ltt-soft-btn h-9 w-9"
                    onClick={handleExportExcel}
                    disabled={loading || (isOverview ? overviewRows.length === 0 : filteredRows.length === 0)}
                    title="Download Excel"
                    aria-label="Download Excel"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  {!isOverview && !isReadOnly ? (
                    <Button type="button" className="ltt-create-btn" onClick={handleAddRow} disabled={loading}>
                      <Plus className="h-4 w-4" />
                      Add row
                    </Button>
                  ) : null}
                </div>
              </section>

              {error ? (
                <div className="ltt-error" role="alert">
                  {error}
                </div>
              ) : null}

              <div className="ltt-lot-tabs" role="tablist" aria-label="Select view">
                {visibleTabs.map((tab) => {
                  const count = tab === TAB_OVERVIEW ? overviewRows.length : lotCounts[tab] || 0;
                  return (
                    <button
                      key={tab}
                      type="button"
                      role="tab"
                      aria-selected={activeLot === tab}
                      className={`ltt-lot-tabs__btn${activeLot === tab ? ' is-active' : ''}`}
                      onClick={() => setActiveLot(tab)}
                    >
                      {tab}
                      <span className="ltt-lot-tabs__count">{count}</span>
                    </button>
                  );
                })}
              </div>

              <div className="ltt-summary-cards">
                <div className="ltt-summary-card" style={{ '--cat-accent': '#1e90ff' }}>
                  <div className="ltt-summary-card__top">
                    <span className="ltt-summary-card__icon" aria-hidden>
                      <Truck className="h-4 w-4" />
                    </span>
                    <span className="ltt-summary-card__label">Total trips</span>
                  </div>
                  <div className="ltt-summary-card__count">
                    {isOverview
                      ? overviewLotFilter === 'all'
                        ? 'All LOTs'
                        : overviewLotFilter
                      : activeLot}
                  </div>
                  <div className="ltt-summary-card__amount">
                    {cards.trips.toLocaleString('en-US')}
                  </div>
                  <div className="ltt-summary-card__meta">
                    <div>
                      <span>View</span>
                      <strong>{isOverview ? 'Spending' : 'Trips'}</strong>
                    </div>
                  </div>
                </div>

                <div className="ltt-summary-card" style={{ '--cat-accent': '#10b981' }}>
                  <div className="ltt-summary-card__top">
                    <span className="ltt-summary-card__icon" aria-hidden>
                      <Route className="h-4 w-4" />
                    </span>
                    <span className="ltt-summary-card__label">Total amount</span>
                  </div>
                  <div className="ltt-summary-card__count">Excl. VAT</div>
                  <div className="ltt-summary-card__amount">
                    TZS {formatMoney(cards.totalAmount)}
                  </div>
                  <div className="ltt-summary-card__meta">
                    <div>
                      <span>VAT portion</span>
                      <strong>TZS {formatMoney(cards.totalWithVat - cards.totalAmount)}</strong>
                    </div>
                  </div>
                </div>

                <div className="ltt-summary-card" style={{ '--cat-accent': '#f59e0b' }}>
                  <div className="ltt-summary-card__top">
                    <span className="ltt-summary-card__icon" aria-hidden>
                      <Receipt className="h-4 w-4" />
                    </span>
                    <span className="ltt-summary-card__label">Total incl. VAT</span>
                  </div>
                  <div className="ltt-summary-card__count">Grand total</div>
                  <div className="ltt-summary-card__amount">
                    TZS {formatMoney(cards.totalWithVat)}
                  </div>
                  <div className="ltt-summary-card__meta">
                    <div>
                      <span>Excl. VAT</span>
                      <strong>TZS {formatMoney(cards.totalAmount)}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className="ltt-table-section__head">
                <h2>
                  {isOverview ? 'Spending by material' : `${activeLot} trips`}
                </h2>
              </div>

              <div className="ltt-toolbar">
                <div className="ltt-search">
                  <Search className="ltt-search__icon h-4 w-4" aria-hidden />
                  <Input
                    className="ltt-search__input"
                    placeholder="Search transporter, material, invoice, destination…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search transport rows"
                  />
                </div>
                <div className="ltt-toolbar__spacer" aria-hidden="true" />
                <div className="ltt-filters">
                  {isOverview ? (
                    <select
                      className="ltt-filter-select"
                      value={overviewLotFilter}
                      onChange={(e) => setOverviewLotFilter(e.target.value)}
                      aria-label="Overview LOT filter"
                    >
                      <option value="all">All LOTs</option>
                      {LOT_OPTIONS.map((lot) => (
                        <option key={lot} value={lot}>
                          {lot}
                        </option>
                      ))}
                    </select>
                  ) : null}
                  {!isOverview && duplicateInvoiceRowCount > 0 ? (
                    <span className="ltt-duplicate-invoice-badge" role="status">
                      {duplicateInvoiceRowCount} duplicate invoice{' '}
                      {duplicateInvoiceRowCount === 1 ? 'number' : 'numbers'}
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="ltt-table-section">
                {loading ? (
                  <div className="ltt-table-shell">
                    <div className="ltt-skel-rows">
                      {Array.from({ length: 6 }, (_, r) => (
                        <Skeleton key={r} className="h-10 w-full rounded-md" />
                      ))}
                    </div>
                  </div>
                ) : isOverview ? (
                  <div className="ltt-table-shell">
                    <div className="ltt-table-wrapper">
                      <table className="ltt-table ltt-overview-table">
                        <thead>
                          <tr>
                            <th>Material</th>
                            <th>Trips</th>
                            <th>Total spend</th>
                            <th>Spend (incl. VAT)</th>
                            <th>% of spend</th>
                          </tr>
                        </thead>
                        <tbody>
                          {overviewRows.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="ltt-empty-row">
                                No spending to summarize yet.
                              </td>
                            </tr>
                          ) : (
                            overviewRows.map((g, i) => {
                              const share = overviewTotals.totalAmount
                                ? (g.totalAmount / overviewTotals.totalAmount) * 100
                                : 0;
                              return (
                                <tr key={`${g.material}-${i}`}>
                                  <td className="ltt-material-name">{g.material}</td>
                                  <td className="ltt-money">{g.trips.toLocaleString('en-US')}</td>
                                  <td className="ltt-money">{formatMoney(g.totalAmount)}</td>
                                  <td className="ltt-money">{formatMoney(g.totalWithVat)}</td>
                                  <td className="ltt-share-cell">
                                    <span className="ltt-share-value">{share.toFixed(1)}%</span>
                                    <span className="ltt-share-bar" aria-hidden>
                                      <span
                                        className="ltt-share-bar-fill"
                                        style={{ width: `${Math.min(share, 100)}%` }}
                                      />
                                    </span>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                        {overviewRows.length > 0 ? (
                          <tfoot>
                            <tr className="ltt-totals-row">
                              <td>Total spend</td>
                              <td className="ltt-money">{overviewTotals.trips.toLocaleString('en-US')}</td>
                              <td className="ltt-money">{formatMoney(overviewTotals.totalAmount)}</td>
                              <td className="ltt-money">{formatMoney(overviewTotals.totalWithVat)}</td>
                              <td className="ltt-money">100%</td>
                            </tr>
                          </tfoot>
                        ) : null}
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="ltt-table-shell">
                    <div className="ltt-table-wrapper">
                      <table className="ltt-table">
                        <thead>
                          <tr>
                            <th className="ltt-col-transporter">Transporter</th>
                            <th className="ltt-col-po">PO</th>
                            <th className="ltt-col-material">Material</th>
                            <th className="ltt-col-invoice">Invoice No</th>
                            <th className="ltt-col-fit">Trips</th>
                            <th className="ltt-col-price">Price / trip</th>
                            <th className="ltt-col-fit">Total</th>
                            <th className="ltt-col-fit">VAT</th>
                            <th className="ltt-col-fit">Total + VAT</th>
                            <th className="ltt-col-destination">Destination</th>
                            <th className="ltt-col-dn">DN No</th>
                            <th className="ltt-col-dn">DN Date</th>
                            <th className="ltt-col-status">Status</th>
                            <th className="ltt-col-date">Date paid</th>
                            <th aria-label="Actions" />
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRows.length === 0 ? (
                            <tr>
                              <td colSpan={15} className="ltt-empty-row">
                                No records for {activeLot} yet. Click “Add row” to start.
                              </td>
                            </tr>
                          ) : (
                            filteredRows.map((row) => {
                              const id = row.documentId;
                              const isNoPo = String(row.po || '').trim().toLowerCase() === 'no po';
                              const invoiceKey = normalizeTruckingInvoiceNo(row.invoiceNo);
                              const ribbonColor = invoiceKey
                                ? invoiceRibbonColors.get(invoiceKey) ?? null
                                : null;
                              return (
                                <tr key={id}>
                                  <td className="ltt-col-transporter">
                                    <input
                                      className="ltt-input"
                                      size={inputSizeFromValue(row.transporter, 4, 24)}
                                      value={row.transporter}
                                      onChange={(e) => handleChange(id, 'transporter', e.target.value)}
                                      placeholder="—"
                                    />
                                  </td>
                                  <td className={isNoPo ? 'ltt-col-po ltt-po-cell ltt-po-nopo' : 'ltt-col-po ltt-po-cell'}>
                                    <input
                                      className="ltt-input"
                                      size={inputSizeFromValue(row.po, 9, 16)}
                                      value={row.po}
                                      onChange={(e) => handleChange(id, 'po', e.target.value)}
                                      placeholder="—"
                                      title={row.po || undefined}
                                    />
                                  </td>
                                  <td className="ltt-col-material">
                                    <input
                                      className="ltt-input"
                                      size={inputSizeFromValue(row.materialTransported, 6, 28)}
                                      value={row.materialTransported}
                                      onChange={(e) => handleChange(id, 'materialTransported', e.target.value)}
                                      placeholder="—"
                                    />
                                  </td>
                                  <LttRibbonCell ribbonColor={ribbonColor} className="ltt-col-invoice">
                                    <input
                                      className="ltt-input"
                                      size={inputSizeFromValue(row.invoiceNo, 6, 20)}
                                      value={row.invoiceNo}
                                      onChange={(e) => handleChange(id, 'invoiceNo', e.target.value)}
                                      placeholder="—"
                                      title={
                                        ribbonColor
                                          ? 'Same invoice number as other row(s)'
                                          : row.invoiceNo || undefined
                                      }
                                    />
                                  </LttRibbonCell>
                                  <td className="ltt-col-fit">
                                    <input
                                      className="ltt-input ltt-input-num"
                                      size={inputSizeFromValue(numericDisplay(id, 'noOfTrucks', row.noOfTrucks), 2, 6)}
                                      inputMode="numeric"
                                      value={numericDisplay(id, 'noOfTrucks', row.noOfTrucks)}
                                      onFocus={() => setFocusedCell(`${id}-noOfTrucks`)}
                                      onBlur={() => setFocusedCell(null)}
                                      onChange={(e) =>
                                        handleChange(
                                          id,
                                          'noOfTrucks',
                                          parseInt(e.target.value.replace(/[^0-9]/g, ''), 10) || 0
                                        )
                                      }
                                      placeholder="0"
                                    />
                                  </td>
                                  <td className="ltt-col-price">
                                    <input
                                      className="ltt-input ltt-input-num"
                                      size={inputSizeFromValue(numericDisplay(id, 'pricePerTrip', row.pricePerTrip), 4, 10)}
                                      inputMode="decimal"
                                      value={numericDisplay(id, 'pricePerTrip', row.pricePerTrip)}
                                      onFocus={() => setFocusedCell(`${id}-pricePerTrip`)}
                                      onBlur={() => setFocusedCell(null)}
                                      onChange={(e) =>
                                        handleChange(
                                          id,
                                          'pricePerTrip',
                                          parseFloat(e.target.value.replace(/[^0-9.]/g, '')) || 0
                                        )
                                      }
                                      placeholder="0.00"
                                    />
                                  </td>
                                  <td className="ltt-money ltt-computed ltt-col-fit">{formatMoney(rowTotalAmount(row))}</td>
                                  <td className="ltt-vat-cell ltt-col-fit">
                                    <label className="ltt-vat-toggle" title="Apply 18% VAT (leave off if exempt)">
                                      <input
                                        type="checkbox"
                                        checked={row.vatApplies}
                                        onChange={(e) => handleChange(id, 'vatApplies', e.target.checked)}
                                      />
                                      <span className="ltt-money ltt-vat-amount">
                                        {row.vatApplies ? formatMoney(rowVat(row)) : '—'}
                                      </span>
                                    </label>
                                  </td>
                                  <td className="ltt-money ltt-computed ltt-total-with-vat ltt-col-fit">
                                    {formatMoney(rowTotalWithVat(row))}
                                  </td>
                                  <td className="ltt-col-destination">
                                    <input
                                      className="ltt-input"
                                      size={inputSizeFromValue(row.destination, 8, 36)}
                                      value={row.destination}
                                      onChange={(e) => handleChange(id, 'destination', e.target.value)}
                                      placeholder="—"
                                      title={row.destination || undefined}
                                    />
                                  </td>
                                  <td className="ltt-col-dn">
                                    <input
                                      className="ltt-input"
                                      size={inputSizeFromValue(row.dnNo, 4, 18)}
                                      value={row.dnNo}
                                      onChange={(e) => handleChange(id, 'dnNo', e.target.value)}
                                      placeholder="—"
                                    />
                                  </td>
                                  <td className="ltt-col-dn">
                                    <input
                                      type="date"
                                      className="ltt-input ltt-input-date"
                                      value={row.dnDate}
                                      onChange={(e) => handleChange(id, 'dnDate', e.target.value)}
                                    />
                                  </td>
                                  <td className="ltt-col-status">
                                    <select
                                      className={`ltt-input ltt-status-select ltt-status-${String(row.status || 'none').toLowerCase()}`}
                                      value={row.status}
                                      onChange={(e) => handleChange(id, 'status', e.target.value)}
                                    >
                                      {STATUS_OPTIONS.map((opt) => (
                                        <option key={opt || 'none'} value={opt}>
                                          {opt || '—'}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="ltt-col-date">
                                    <input
                                      type="date"
                                      className="ltt-input ltt-input-date"
                                      value={row.datePaid}
                                      disabled={row.status !== 'Paid'}
                                      onChange={(e) => handleChange(id, 'datePaid', e.target.value)}
                                    />
                                  </td>
                                  <td className="ltt-action-cell">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="ltt-soft-btn h-8 w-8"
                                      title="Delete row"
                                      aria-label="Delete row"
                                      onClick={() => handleDeleteRow(row)}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                        {filteredRows.length > 0 ? (
                          <tfoot>
                            <tr className="ltt-totals-row">
                              <td colSpan={4}>Totals</td>
                              <td className="ltt-money">{totals.trucks.toLocaleString('en-US')}</td>
                              <td />
                              <td className="ltt-money">{formatMoney(totals.totalAmount)}</td>
                              <td className="ltt-money">{formatMoney(totals.vat)}</td>
                              <td className="ltt-money">{formatMoney(totals.totalWithVat)}</td>
                              <td colSpan={6} />
                              <td />
                            </tr>
                          </tfoot>
                        ) : null}
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default LocalTransportationTrucking;
